import type {ReactNode} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';

/** Preserve thread/chat selection and one-shot Otto handoffs on old bookmarks. */
export function LegacyInboxRedirect() {
  const {search, hash} = useLocation();
  const state = useLocation().state as unknown;
  return <Navigate to={`/inbox${search}${hash}`} state={state} replace />;
}

export function QuotingHomeRedirect({children}: {children: ReactNode}) {
  const {loading, enabled} = useQuotingAvailability();
  const quotesFirst = useQuotesFirst();
  // An AES tenant keeps its own shell for now (a temporary split, to be consolidated):
  // Home and Library stay where they were.
  if (loading || quotesFirst === undefined) return null;
  if (quotesFirst) return <>{children}</>;
  return enabled ? <Navigate to="/quotes" replace /> : <>{children}</>;
}

/** Saved scopes retire with the quoting shell; direct evidence links keep their target. */
export function QuotingLibraryRedirect({children}: {children: ReactNode}) {
  const {loading, enabled} = useQuotingAvailability();
  const quotesFirst = useQuotesFirst();
  const {search} = useLocation();
  const state = useLocation().state as unknown;
  if (loading || quotesFirst === undefined) return null;
  if (quotesFirst) return <>{children}</>;
  if (!enabled) return <>{children}</>;
  const params = new URLSearchParams(search);
  const scope = params.get('scope')?.split(',')[0]?.split(':');
  if (scope?.length === 2 && scope[1] !== '*') {
    params.set('type', scope[0]);
    params.set('record', scope[1]);
  }

  const hasSource =
    params.has('thread') || params.has('file') || params.has('record');
  const destination = hasSource
    ? '/inbox/source'
    : params.has('chat')
      ? '/inbox'
      : '/quotes';
  return (
    <Navigate
      to={`${destination}${params.size > 0 ? `?${params.toString()}` : ''}`}
      state={state}
      replace
    />
  );
}
